import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from '@yema/commerce/StoreClient';
import { get_ } from 'sf-modules';
import { initialize } from 'utils/initialize';
import initializeUserContext from 'utils/initializeUserContext';
import { addApolloState } from '@yema/commerce/lib/apollo';
import { trackError } from '@yema/commerce/utils/error';
import { getMenus } from 'functions/fnRequestAPIs/fnMenus';
import { getProductOrBundle } from 'functions/fnRequestAPIs/fnProductsAPI';
import { getConfigPage } from 'functions/fnRequestAPIs/fnConfigPage';
import LoaderLogo from '@/yema-designkit/webkit/atoms/Loaders/LoaderLogo';
import PageLayout from 'layouts/Page.layout';
import NotFoundLayout from 'layouts/NotFound.layout';
import ProductPage from 'components/products/ProductPage';
import Error500 from '@yema/commerce/components/checkout/Error500';

const ProductPageDetails = ({ pageContent, staticContent, uspPromise, uspPromo }) => {
    const router = useRouter();
    const authToken = useSelector(({ authentication }) => authentication.token);
    const dispatch = useDispatch();

    useEffect(() => initializeUserContext({ authToken, dispatch }), [authToken, router.asPath]);

    //@TODO Ludo: should show product page skeleton
    if (router.isFallback)
        return (
            <LoaderLogo
                position='fixed'
                overlayColor='rgba(253, 242, 241, .9)'
                withOverlay
                active
            />
        );

    if (pageContent && pageContent.error) {
        trackError({
            label: 'Fail to load product page',
            extras: {
                productSlug: router.query.productName,
                data: pageContent,
                error: pageContent.error,
            },
        });
        return (
            <Error500
                title='Error al cargar la página del producto'
                extrasToTrack={{
                    errorType: 'Fail to load product page',
                    errorDescription: pageContent?.error?.message,
                }}
                fullPage
            />
        );
    }

    if (!pageContent || !pageContent.product)
        return <NotFoundLayout staticContent={staticContent} />;

    return (
        <PageLayout staticContent={staticContent}>
            <ProductPage {...pageContent} uspPromo={uspPromo} uspPromise={uspPromise} />
        </PageLayout>
    );
};

export async function getStaticProps(context) {
    try {
        const { reduxStore, apolloClient } = await initialize({
            withReduxStore: true,
            withApolloClient: true,
            withUserContext: false,
        });

        const menus = await getMenus(context, apolloClient);
        const page = await getProductOrBundle({
            client: apolloClient,
            slug: context.params.productName,
            isBundle: false,
        });

        if (!page || !page.product || page?.product?.itemType === 'COMPONENT')
            return { notFound: true };

        const product = get_(page, ['product']);

        const configPage = await getConfigPage();
        const uspPromo = get_(configPage, ['uspPromo']);
        const uspPromise = get_(configPage, ['uspPromise']);

        const metaTags = product
            ? {
                  title: product.name,
                  description: product.seoDescription,
                  image: get_(product, ['images', 'mainUrl']),
                  productTags: {
                      brand: product.brand,
                      hasStock: true, // @TODO Ludo: how should we handle stock metatags with new zipcode availability strategy?
                      price: product.pricing.price,
                      currency: 'MXN',
                      id: product.gtin,
                  },
              }
            : {};

        return addApolloState(apolloClient, {
            props: {
                initialReduxState: reduxStore.getState(),
                revalidatedOn: Date.now(),
                pageContent: page,
                uspPromo,
                uspPromise,
                staticContent: {
                    ...menus,
                    ...configPage,
                    metaTags,
                    preview: !!context.preview,
                },
            },

            // Re-generate the page:
            // - when a request comes in
            // - at most once every X seconds
            revalidate: 60,
        });
    } catch (error) {
        // The API most likely died
        console.error(error);
        return {
            props: {},
        };
    }
}

export async function getStaticPaths() {
    // const allPages = []; //await getProductSlugs();
    return {
        // paths: allPages?.map(slug => `/p/${slug}`) || [],
        paths: [],
        fallback: 'blocking',
    };
}

export default ProductPageDetails;
